import { useState, useEffect } from "react";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import java from "react-syntax-highlighter/dist/esm/languages/hljs/java";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import backend from "../../../../../backend/backend";
import SlidePanel from "../../../../shared/SlidePanel";
import Spinner from "../../../../../assets/icons/Spinner";
import { currentModelDescriptions } from "../../../../../model/model-providers/modelDescriptors";
import "./RequestLogDetailPanel.scss";

const getModelDisplayName = (modelId) => {
  if (!modelId) return "—";
  const descriptor = currentModelDescriptions.find((m) => m.id === modelId);
  return descriptor?.name || modelId;
};

// Register languages for syntax highlighting
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("python", python);

// Parse request body and extract messages array
const parseRequestContent = (jsonString) => {
  if (!jsonString) return null;
  try {
    const data = JSON.parse(jsonString);
    if (data.messages && Array.isArray(data.messages)) {
      return { type: "messages", messages: data.messages, model: data.model };
    }
    return { type: "raw", content: jsonString };
  } catch {
    return { type: "text", content: jsonString };
  }
};

// Parse streaming response (NDJSON format - newline-delimited JSON chunks)
const parseStreamingResponse = (jsonString) => {
  const lines = jsonString.trim().split("\n");
  if (lines.length < 2) return null;

  let content = "";
  let role = "assistant";
  let model = null;
  let usage = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith("{")) continue;

    try {
      const chunk = JSON.parse(trimmed);

      // Extract model from first chunk
      if (!model && chunk.model) {
        model = chunk.model;
      }

      // Extract content from delta
      if (chunk.choices && Array.isArray(chunk.choices) && chunk.choices.length > 0) {
        const delta = chunk.choices[0]?.delta;
        if (delta) {
          if (delta.role) role = delta.role;
          if (delta.content) content += delta.content;
        }
      }

      // Extract usage from final chunk
      if (chunk.usage) {
        usage = chunk.usage;
      }
    } catch {
      // Skip invalid JSON lines
      continue;
    }
  }

  // Only return streaming result if we actually extracted content
  if (content) {
    return {
      type: "streaming",
      content,
      role,
      model,
      usage,
    };
  }

  return null;
};

// Parse response body and extract choices
const parseResponseContent = (jsonString) => {
  if (!jsonString) return null;

  // First, try to parse as a single JSON object
  try {
    const data = JSON.parse(jsonString);
    if (data.choices && Array.isArray(data.choices)) {
      return { type: "choices", choices: data.choices, model: data.model, usage: data.usage };
    }
    return { type: "raw", content: jsonString };
  } catch {
    // If single JSON parse fails, try streaming format (NDJSON)
    const streamingResult = parseStreamingResponse(jsonString);
    if (streamingResult) {
      return streamingResult;
    }

    // Fallback to plain text
    return { type: "text", content: jsonString };
  }
};

// Custom markdown components with syntax highlighting for code blocks
const markdownComponents = {
  code(props) {
    const { children, className, ...rest } = props;
    const match = /language-(\w+)/.exec(className || "");

    return match ? (
      <SyntaxHighlighter
        {...rest}
        style={atomOneDark}
        language={match[1]}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: "6px", fontSize: "13px" }}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code {...rest} className={className}>
        {children}
      </code>
    );
  },
};

// Render markdown content with syntax highlighting
const MarkdownContent = ({ children }) => (
  <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
    {children}
  </Markdown>
);

// Render a single chat message bubble
const MessageBubble = ({ role, content }) => (
  <div className={`message_bubble ${role}`}>
    <div className="message_role">{role}</div>
    <div className="message_content">
      <MarkdownContent>{content}</MarkdownContent>
    </div>
  </div>
);

// Render request content (messages list or plain text)
const RequestContent = ({ data }) => {
  if (!data) return <div className="no_data">No request body available</div>;

  if (data.type === "messages") {
    return (
      <div className="messages_list">
        {data.messages.map((msg, idx) => (
          <MessageBubble key={idx} role={msg.role} content={msg.content || ""} />
        ))}
      </div>
    );
  }

  // Fallback to markdown-rendered text with syntax highlighting
  return (
    <div className="plain_content">
      <MarkdownContent>{data.content}</MarkdownContent>
    </div>
  );
};

// Render response content (choices or plain text)
const ResponseContent = ({ data }) => {
  if (!data) return <div className="no_data">No response body available</div>;

  if (data.type === "choices") {
    return (
      <div className="messages_list">
        {data.choices.map((choice, idx) => {
          const msg = choice.message;
          return (
            <div key={idx}>
              <MessageBubble role={msg?.role || "assistant"} content={msg?.content || ""} />
              {msg?.tool_calls?.length > 0 && (
                <div className="tool_calls_section">
                  <div className="tool_calls_label">Tool Calls:</div>
                  {msg.tool_calls.map((tc, i) => (
                    <div key={i} className="tool_call_item">
                      <span className="tool_name">{tc.function?.name}</span>
                      <code>{tc.function?.arguments}</code>
                    </div>
                  ))}
                </div>
              )}
              {msg?.reasoning_content && (
                <div className="reasoning_section">
                  <div className="reasoning_label">Reasoning:</div>
                  <MarkdownContent>{msg.reasoning_content}</MarkdownContent>
                </div>
              )}
            </div>
          );
        })}
        {data.usage && (
          <div className="usage_summary">
            Tokens: {data.usage.prompt_tokens} prompt + {data.usage.completion_tokens} completion = {data.usage.total_tokens} total
          </div>
        )}
      </div>
    );
  }

  // Handle streaming response (reconstructed from NDJSON chunks)
  if (data.type === "streaming") {
    return (
      <div className="messages_list">
        <MessageBubble role={data.role} content={data.content} />
        {data.usage && (
          <div className="usage_summary">
            Tokens: {data.usage.prompt_tokens} prompt + {data.usage.completion_tokens} completion = {data.usage.total_tokens} total
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="plain_content">
      <MarkdownContent>{data.content}</MarkdownContent>
    </div>
  );
};

// Tab component for switching between Content and Source views
const ContentSourceTabs = ({ activeTab, onTabChange }) => (
  <div className="content_source_tabs">
    <div className="tab_group">
      <div
        className={`tab_group_item${activeTab === "content" ? " selected" : ""}`}
        onClick={() => onTabChange("content")}
      >
        Content
      </div>
      <div
        className={`tab_group_item${activeTab === "source" ? " selected" : ""}`}
        onClick={() => onTabChange("source")}
      >
        Source
      </div>
    </div>
  </div>
);

const RequestLogDetailPanel = ({ logUid, isOpen, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestTab, setRequestTab] = useState("content");
  const [responseTab, setResponseTab] = useState("content");

  useEffect(() => {
    if (!isOpen || !logUid) return;

    setLoading(true);
    setError(null);

    backend
      .getGatewayLogDetail(logUid)
      .then((data) => {
        setDetail(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load log detail:", err);
        setError("Failed to load request details");
        setLoading(false);
      });
  }, [logUid, isOpen]);

  const formatJson = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const formatDuration = (ms) => {
    if (ms === null || ms === undefined) return "—";
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  };

  const formatTokens = (tokens) => {
    if (tokens === null || tokens === undefined) return "—";
    return tokens.toLocaleString();
  };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="Request Details" width="60%">
      {loading && (
        <div className="detail_loading">
          <Spinner />
        </div>
      )}

      {error && (
        <div className="detail_error">
          <p>{error}</p>
        </div>
      )}

      {detail && !loading && (
        <div className="request_log_detail">
          {/* Metadata Section */}
          <section className="detail_section">
            <h4>Request Information</h4>
            <div className="detail_grid">
              <div className="detail_item">
                <label>Date</label>
                <span>{new Date(detail.dateCreated).toLocaleString()}</span>
              </div>
              <div className="detail_item">
                <label>Model</label>
                <span title={detail.modelId}>{getModelDisplayName(detail.modelId)}</span>
              </div>
              <div className="detail_item">
                <label>Request UID</label>
                <span className="mono">{detail.uid}</span>
              </div>
              <div className="detail_item">
                <label>Gateway UID</label>
                <span className="mono">{detail.gatewayUid}</span>
              </div>
            </div>
          </section>

          {/* Performance Metrics */}
          <section className="detail_section">
            <h4>Performance Metrics</h4>
            <div className="detail_grid">
              <div className="detail_item">
                <label>Time to First Token</label>
                <span>{detail.ttftMs !== null ? `${detail.ttftMs} ms` : "—"}</span>
              </div>
              <div className="detail_item">
                <label>Total Duration</label>
                <span>{formatDuration(detail.durationMs)}</span>
              </div>
              <div className="detail_item">
                <label>Tokens/Second</label>
                <span>{detail.tokensPerSecond?.toFixed(1) ?? "—"}</span>
              </div>
              <div className="detail_item">
                <label>Cache Hit</label>
                <span>{detail.cacheReadHit ? "Yes" : "No"}</span>
              </div>
            </div>
          </section>

          {/* Token Usage */}
          <section className="detail_section">
            <h4>Token Usage</h4>
            <div className="detail_grid">
              <div className="detail_item">
                <label>Input Tokens</label>
                <span>{formatTokens(detail.inputTokens)}</span>
              </div>
              <div className="detail_item">
                <label>Cached Input Tokens</label>
                <span>{formatTokens(detail.inputCachedTokens)}</span>
              </div>
              <div className="detail_item">
                <label>Output Tokens</label>
                <span>{formatTokens(detail.outputTokens)}</span>
              </div>
              <div className="detail_item">
                <label>Reasoning Tokens</label>
                <span>{formatTokens(detail.reasoningTokens)}</span>
              </div>
            </div>
          </section>

          {/* Request Body */}
          <section className="detail_section">
            <h4>Request Body</h4>
            <ContentSourceTabs activeTab={requestTab} onTabChange={setRequestTab} />
            {requestTab === "content" ? (
              <div className="content_tab_body">
                <RequestContent data={parseRequestContent(detail.requestBody)} />
              </div>
            ) : (
              <div className="json_container">
                {detail.requestBody ? (
                  <SyntaxHighlighter
                    language="json"
                    style={atomOneDark}
                    customStyle={{
                      margin: 0,
                      borderRadius: "8px",
                      fontSize: "13px",
                    }}
                  >
                    {formatJson(detail.requestBody)}
                  </SyntaxHighlighter>
                ) : (
                  <div className="no_data">No request body available</div>
                )}
              </div>
            )}
          </section>

          {/* Response Body */}
          <section className="detail_section">
            <h4>Response Body</h4>
            <ContentSourceTabs activeTab={responseTab} onTabChange={setResponseTab} />
            {responseTab === "content" ? (
              <div className="content_tab_body">
                <ResponseContent data={parseResponseContent(detail.responseBody)} />
              </div>
            ) : (
              <div className="json_container">
                {detail.responseBody ? (
                  <SyntaxHighlighter
                    language="json"
                    style={atomOneDark}
                    customStyle={{
                      margin: 0,
                      borderRadius: "8px",
                      fontSize: "13px",
                    }}
                  >
                    {formatJson(detail.responseBody)}
                  </SyntaxHighlighter>
                ) : (
                  <div className="no_data">No response body available</div>
                )}
              </div>
            )}
          </section>

          {/* Close Button */}
          <div className="detail_footer">
            <button className="close_btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
};

export default RequestLogDetailPanel;
